// src/components/StudioEditorPage/Pickers/AvailabilityEditor.jsx
import { useStudioAvailabilities } from "../../../hooks/calendar/useStudioAvailabilities";
import { useFetchStudioData } from "../../../hooks/calendar/useFetchStudioData";
import AvailabilityModal from "../../CalendarPage/AvailabilityModal";
import { useRef, useImperativeHandle, forwardRef, useState, useEffect } from "react";

export const AvailabilityEditor = forwardRef(({ studio, validate }, ref) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [error, setError] = useState(null);
  
  // Setup validation function to be used by EditorContainer
  useEffect(() => {
    if (validate && typeof validate.current !== 'undefined') {
      validate.current = () => {
        return !error; // Return true if there are no errors
      };
    } else {
    }
  }, [validate, error]);
  
  const { 
    availabilities, 
    loading: availabilitiesLoading, 
    error: availabilitiesError, 
    refetch: refetchAvailabilities,
    createAvailability,
    deleteAvailability
  } = useStudioAvailabilities(studio?.id);

  const { 
    studioData: bookingData, 
    loading: bookingDataLoading, 
    error: bookingDataError
  } = useFetchStudioData(studio?.userID);

  // Find the specific studio data from all studios data
  const currentStudioBookings = bookingData?.find(s => s.studioId === studio?.id)?.bookings || [];

  const modalRef = useRef();

  // Force refresh after component mounts or when studio ID changes
  useEffect(() => {
    if (studio?.id) {
      const fetchData = async () => {
        await refetchAvailabilities();
      };
      fetchData();
    }
  }, [studio?.id, refreshKey]);
  
  // Expose the modal's handleSave method correctly to parent
  useImperativeHandle(ref, () => {
    return {
      handleSave: async () => {
        if (modalRef.current) {
          try {
            // Directly call the handleSave method and await its result
            const result = await modalRef.current.handleSave();
            // Force a refresh after saving
            if (result) {
              setRefreshKey(prev => {
                return prev + 1;
              });
              setError(null);
              await refetchAvailabilities();
            }
            
            return result;
          } catch (error) {
            setError("Verfügbarkeit konnte nicht gespeichert werden.");
            throw error;
          }
        }
        return false;
      },
      // Expose hasErrors function for the parent to check
      hasErrors: () => {
        return !!error;
      }
    };
  }, [error, refetchAvailabilities]);

  if (availabilitiesLoading || bookingDataLoading) {
    return <div>Wird geladen...</div>;
  }

  // Create a modified version of onSave that updates the error state
  const handleModalError = (errorMessage) => {
    setError(errorMessage);
  };

  return (
    <AvailabilityModal 
      ref={modalRef}
      bookingData={currentStudioBookings}
      minNumberOfHours={studio?.minNumberOfHours}
      key={`${studio?.id}-${refreshKey}`} // Add refreshKey to force re-render
      studioData={availabilities} 
      studioId={studio?.id} 
      onSave={() => {
        refetchAvailabilities();
        setRefreshKey(prev => {
          return prev + 1;
        });
      }}
      onError={handleModalError}
      fromEditor={true}
      createAvailability={createAvailability} 
      deleteAvailability={deleteAvailability}
    />
  );
});

export default AvailabilityEditor;
